import { FormControl, FormHelperText, InputLabel, MenuItem, Select } from "@mui/material";
import { Box } from "@mui/system";
import { Contact, ContactType, useGetMyContractorsLazyQuery } from "@repo/server-api";
import { RuleFactoryType, TabKeys } from "@repo/server-api/types";
import { useNotification, PersonIdentification  } from "@repo/shared/components";
import React, { useState, useEffect, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { emptyContact, validErrorState } from "@repo/legacy/components/helper/Index";
import {
	useCreatePersonContact,
	useUpdatePersonContact,
} from "@repo/legacy/components/hooks/ContactMutationHooks";
import ContactInfo from "@repo/legacy/components/shared/additionalInformation/ContactInfo";
import SalaryInformation from "@repo/legacy/components/shared/additionalInformation/SalaryInformation";

import DialogTabs from "./DialogWithTabs";

export default function DialogCUEmployee(
	props: Readonly<{
		open: boolean;
		setOpen: (value: boolean) => void;
		handleClose?: (value: boolean) => void;
		refreshData: (member: Contact) => void;
		contactType: ContactType;
		contact?: Contact | null;
		showContractorSelection?: boolean;
		contractorId?: string | null;
		ruleFactory: RuleFactoryType;
	}>,
) {
	const { t } = useTranslation();

	const [contact, setContact] = useState<Contact | null>(null);
	const [createPersonContact] = useCreatePersonContact();
	const [updatePersonContact] = useUpdatePersonContact();

	const [tabIndex, setTabIndex] = useState<number>(0);
	const open = useMemo<boolean>(() => {
		setTabIndex(0);
		return props.open;
	}, [props.open]);

	const [errors, setErrors] = useState<Record<string, object | string | boolean>>({});

	const [contractorId, setContractorId] = useState<string | null>(props.contractorId || null);

	const { showNotification } = useNotification();

	const [contractors, setContractors] = useState<Contact[]>([]);

	const [getAllContractors] = useGetMyContractorsLazyQuery({
		fetchPolicy: "no-cache",
		variables: { first: 100 },
		onCompleted(data) {
			if (data.myContractors?.nodes) {
				setContractors(data.myContractors.nodes as unknown as Contact[]);
			}
		},
		onError(error) {
			showNotification(error.message, "error");
		},
	});

	useEffect(() => {
		if (open && props.showContractorSelection) getAllContractors();
	}, [open]);

	useEffect(() => {
		if (props.contact) {
			setContact({
				...props.contact,
				contactTypes: [
					...(props.contact.contactTypes || []).filter(
						(type) => type !== ContactType.Person && type !== props.contactType,
					),
					ContactType.Person,
					props.contactType,
				],
			});
		} else {
			setContact({
				...emptyContact,
				contactTypes: [ContactType.Person, props.contactType],
			});
		}
	}, [props.contact]);

	const handleClose = () => {
		props.setOpen(false);
		setErrors({});
		setSaveClicked(false);
	};

	const [saveClicked, setSaveClicked] = useState<boolean>(false);

	const personIdentificationRef = useRef<{
		validate: () => Promise<Record<string, any>>;
	} | null>(null);
	const contactInfoRef = useRef<{
		validate: () => Promise<Record<string, any>>;
	} | null>(null);
	const workingConditionsRef = useRef<{
		validate: () => Promise<Record<string, any>>;
	} | null>(null);
	const salaryInformationRef = useRef<{
		validate: () => Promise<Record<string, any>>;
	} | null>(null);

	const validateFields = async () => {
		const localErrors: Record<string, any> = {};

		localErrors.personIdentification = await personIdentificationRef.current?.validate();
		localErrors.contactInfo = await contactInfoRef.current?.validate();
		localErrors.workingConditions = await workingConditionsRef.current?.validate();
		localErrors.salaryInformation = await salaryInformationRef.current?.validate();

		if (props.showContractorSelection && !contractorId) {
			localErrors.contractorSelection = t("validation.contractor.required");
		}

		setErrors({ ...localErrors });

		return validErrorState(localErrors);
	};

	const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		event.stopPropagation();

		const isValid = await validateFields();

		if (isValid) {
			setSaveClicked(true);
			if (contact?.id) {
				updatePersonContact(contact).then((res) => {
					props.refreshData(res);
					handleClose();
				});
			} else {
				createPersonContact(contact, contractorId).then((res) => {
					props.refreshData(res);
					handleClose();
				});
			}
		}
	};

	const tabs = useMemo(
		() => [
			{
				label: t("tab.contact-information"),
				key: TabKeys.CONTACT_INFORMATION,
				content: (
					<ContactInfo
						ref={contactInfoRef}
						errors={errors.contactInfo as Record<string, object | string | boolean>}
						setErrors={(value) =>
							setErrors({
								...errors,
								contactInfo: {
									...(errors.contactInfo as Record<string, object | string | boolean>),
									...value,
								},
							})
						}
						contact={contact}
						setContact={setContact}
					/>
				),
			},
			{
				label: t("tab.salary-information"),
				key: TabKeys.SALARY_INFORMATION,
				content: (
					<SalaryInformation
						ref={salaryInformationRef}
						errors={
							errors.salaryInformation as Record<string, object | string | boolean> | undefined
						}
						setErrors={(value) =>
							setErrors({
								...errors,
								salaryInformation: {
									...(errors.salaryInformation as Record<string, object | string | boolean>),
									...value,
								},
							})
						}
						contact={contact}
						setContact={setContact}
					/>
				),
			},
		],

		[t, contact, errors],
	);

	const personIdentificationTab = {
		label: t("tab.person-identification"),
		key: TabKeys.PERSON_IDENTIFICATION,
		content: (
			<PersonIdentification
				isDialog={true}
				ref={personIdentificationRef}
				errors={
					errors.personIdentification as Record<string, object | string | boolean> | undefined
				}
				setErrors={(value) =>
					setErrors({
						...errors,
						personIdentification: {
							...(errors.personIdentification as Record<string, object | string | boolean>),
							...value,
						},
					})
				}
				contact={contact}
				setContact={setContact}
			/>
		),
	};
	const allTabs = useMemo(() => {
		if (props.showContractorSelection) {
			return [
				personIdentificationTab,
				{
					label: t("contractor"),
					key: TabKeys.CONTRACTOR_SELECTION,
					content: (
						<Box className="w-100">
							<FormControl className="left w-60">
								<InputLabel className={errors?.contractorSelection ? "red" : ""} id="type">
									{t("select-contractor")}
								</InputLabel>
								<Select
									value={contractorId}
									onChange={(e) => {
										setContractorId(e.target.value as string);
										setErrors({
											...errors,
											contractorSelection: "",
										});
									}}
									error={!!errors.contractorSelection}
									displayEmpty
									fullWidth>
									{contractors.map((contractorOption) => (
										<MenuItem key={contractorOption.id} value={contractorOption.id}>
											{contractorOption.displayName}
										</MenuItem>
									))}
								</Select>
								{errors.contractorSelection && (
									<FormHelperText className="red">
										{errors.contractorSelection as string}
									</FormHelperText>
								)}
							</FormControl>
						</Box>
					),
				},
				...tabs,
			];
		}
		return [personIdentificationTab, ...tabs];
	}, [tabs, contractors, contractorId, errors, t, props.showContractorSelection]);

	return (
		<DialogTabs
			open={open}
			handleSubmit={handleSubmit}
			handleClose={handleClose}
			setValue={setTabIndex}
			value={tabIndex}
			title={
				contact?.id
					? props.contactType === ContactType.Employee
						? t("employee.edit")
						: t("edit-freelancer")
					: props.contactType === ContactType.Employee
						? t("employee.new")
						: t("new-freelancer")
			}
			tabs={allTabs}
			errors={errors}
			saveClicked={saveClicked}
			ruleFactory={props.ruleFactory}
			testId="cu-employee-dialog"
		/>
	);
}
